(ns common.schema.entities.oppo.schema
  (:require
   [common.country :as country]
   [common.currencies :as currencies]
   [common.schema.assets :as assets]
   [common.schema.associations :as associations]
   [common.schema.constraints :as constraints]
   [common.schema.entities.common :as common-schema]
   [common.schema.entities.oppo.time-fields :as time-fields]
   [common.schema.option-filters :as option-filters]
   [common.schema.schema-utils :as su]
   [common.status :as statuses]
   [common.time :as ct]
   [common.util :as cu]
   [potpuri.core :as potpuri]))

(def target-asset-type-labels
  {:entity.type/company     "Company"
   :entity.type/fof-asset   "Fund"
   :entity.type/other-asset "Generic asset"
   :entity.type/real-estate "Real estate"})

(def import-fixed-fields
  [:common/external-id
   :common/created-on
   :oppo/name
   :oppo/asset-type
   :oppo/target-asset
   :oppo/pipeline
   :oppo/stage
   :oppo/status
   :oppo/lead-type-v2
   :oppo/lead-source-v2
   ;; pass related
   :oppo/pass-reason-v2 :oppo/passed-on :oppo/last-pass-comment
   ;; on hold related
   :oppo/put-on-hold-on :oppo/on-hold-until :oppo/last-hold-comment])

(defn- oldest-association-triggers
  "The `:calculation-triggers` of the `:oppo/calc-oldest-*` fields. The field reads the
   associations of the oppo and the entity at the other end of each of them. The oppo
   is the source or the target of an association, so both references are followed.
   - A change of an association builds the documents of its two ends again, with the
     oppo among them.
   - A change of the entity at the other end (`entity-type`, a company or a person)
     goes through its associations to the oppo. The hop to the association is
     reversed, and the hop from the association to the oppo is not."
  [entity-type]
  {:entity.type/entity-association [[:entity-association/source-entity]
                                    [:entity-association/target-entity]]
   entity-type                     [[:entity-association/_source-entity :entity-association/target-entity]
                                    [:entity-association/_target-entity :entity-association/source-entity]]})

(def owner-field
  {:field/data-type             :data.type/ref-single
   :field/system-semantics      su/system-prefilled-required-field-semantics
   :field/system-required?      true
   :field/not-clearable?        true
   :field/default-ord           40
   :value-type                  :entity
   :field/entity-types          [:entity.type/account]
   :field/default-visibility    {:editor           :editor.type/select
                                 :grid             :editor.type/select
                                 :filter           :filter.type/select
                                 :field-visibility :in-use-disabled}
   :field/default-authorization {:access/read  :role/guest-buy-side
                                 :access/write :role/user}
   :field/translations          {:en "Owner"}
   :field/option-filter-fn      option-filters/active-users
   :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                  {"extras.active-user" true})})

(def schema
  (vec
   (concat
    (su/option-field :oppo/priority "Priority" {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/IRL "IRL" {:authorization su/access-bsg-bsg})
    (su/boolean-field :oppo/company-is-applicant?
                      "Company is funding applicant?"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use-blocked})
    (su/text-simple-field :oppo/location-city "City" {:authorization su/access-bsg-bsg})
    (su/text-simple-field :oppo/location-region "Region" {:authorization su/access-bsg-bsg})
    (su/text-simple-field :oppo/details-about-other-funding-sources
                          "Details of other funding sources"
                          {:authorization su/access-bsg-bsg})
    (su/currency-field :oppo/cur-proposed-equity-round-size
                       "Proposed equity round size"
                       {:authorization            su/access-bsg-bsg
                        :default-field-visibility :not-in-use-blocked})
    (su/currency-field :oppo/cur-other-funding
                       "Other funding size"
                       {:authorization su/access-bsg-bsg})
    (su/currency-field :oppo/cur-co-investment "Co-investment" {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/status-of-other-funding
                     "Status of other funding"
                     {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/treshold-1 "Precondition I" {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/treshold-2 "Precondition II" {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/treshold-3 "Precondition III" {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/treshold-comments
                             "Treshold comments"
                             {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/investment-category
                     "Investment category"
                     {:authorization su/access-bsg-bsg})
    (su/option-field-multi :oppo/funding-type "Funding type" {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/investment-type "Investment type" {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/investment-segment
                     "Investment segment"
                     {:authorization su/access-bsg-bsg})
    (su/option-field-multi :oppo/investment-segments
                           "Investment segments"
                           {:authorization su/access-bsg-bsg})
    (su/option-field-multi :oppo/added-state-value
                           "Added state value"
                           {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/primary-impact "Primary impact" {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/primary-impact-comment
                             "Primary impact comment"
                             {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/impact-magnitude
                             "Impact magnitude"
                             {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/investment-specific-impact
                             "Investment specific impact"
                             {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/analysis-argumentation
                             "Analysis argumentation"
                             {:authorization su/access-bsg-bsg})
    (su/boolean-field :oppo/existing-customer
                      "Existing customer?"
                      {:authorization su/access-bsg-bsg})

    [[:oppo/pipeline
      {:field/data-type             :data.type/ref-single
       :field/not-clearable?        true
       :field/mandatory             :mandatory/always
       :field/system-semantics      su/user-provided-system-required-field-semantics
       :field/system-required?      true
       :value-type                  :entity
       :field/entity-types          [:entity.type/pipeline]
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/user}
       :field/in-use-for-this-data? su/pipeline-in-use?
       :field/translations          {:en "Pipeline"}
       :field/option-filter-fn      (partial option-filters/pipelines :entity.type/oppo)
       :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                      {"original.pipeline.entity-type" :entity.type/oppo})}]

     [:oppo/use-of-proceeds
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1260
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/placeholder           "Add description"
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Use of proceeds description"}}]
     [:oppo/accessibility
      {:field/data-type             :data.type/text-simple
       :field/default-visibility    {:editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Deal accessibility"}}]

     [:oppo/target-closing-time
      {:field/data-type             :data.type/text-simple
       :field/default-visibility    {:editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Target closing date"}}]

     [:oppo/financing-need
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1070
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Financing needs"}}]

     [:oppo/asset-type
      ;; THIS FIELD IS AN artificial field, only used during import
      {:field/data-type                  :data.type/keyword
       :field/default-visibility         {:editor           :editor.type/none
                                          :grid             :editor.type/none
                                          :filter           :filter.type/none
                                          :field-visibility :in-use-disabled}
       :field/default-authorization      {:access/read  :role/system
                                          :access/write :role/admin}
       :field/hidden-from-configuration? true
       :options                          (->> assets/target-asset-type-mappings
                                              :entity.type/oppo
                                              (map (fn [et]
                                                     {:key   et
                                                      :label (get target-asset-type-labels
                                                                  et
                                                                  (name et))}))
                                              (vec))
       :field/translations               {:en "Asset type"}}]
     [:oppo/founders-and-team-description
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1090
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Founders & team description"}}]

     [:oppo/evaluation-and-conclusion
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1540
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Evaluation and conclusion"}}]

     [:oppo/interaction-history
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           980
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Interaction history"}}]

     [:oppo/deal-notes-url
      {:field/data-type             :data.type/text-url
       :field/default-ord           1210
       :field/default-visibility    {:editor           :editor.type/url
                                     :grid             :editor.type/url
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Deal notes in Evernote"}}]

     [:oppo/lead-source-comment
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1130
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Source comment"}}]

     [:oppo/datetime-estimated-timing
      {:field/data-type             :data.type/date
       :field/default-ord           880
       :field/default-visibility    {:editor           :editor.type/date
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Estimated timing"}}]

     [:oppo/name
      {:field/data-type             :data.type/text-simple
       :field/default-ord           10
       :field/mandatory             :mandatory/always
       :field/name-attribute        :oppo/name
       :field/system-required?      true
       :field/system-semantics      su/user-provided-system-required-field-semantics
       :field/lower-case-str?       true
       :field/constraints           {:constraint/mandatory-fn      (fn [field-value _entity-data]
                                                                     (empty? (str field-value)))
                                     :constraint/string-min-length 1
                                     :constraint/unique-tenant-ignore-case
                                     {:validation-fn (partial constraints/tenant-unique-ignore-case
                                                              :oppo/name
                                                              [:entity.type/oppo])}}
       :field/default-visibility    {:field-visibility :in-use-disabled
                                     :editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/user}
       :field/not-clearable?        true
       :field/translations          {:en "Deal name"}}]

     [:oppo/datetime-investment-date-actual
      {:field/data-type             :data.type/datetime
       :field/default-ord           960
       :field/default-visibility    {:editor           :editor.type/date
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Investment date actual"}}]
     [:oppo/five-biggest-competitors
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1050
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Five biggest competitors"}}]
     [:oppo/datetime-submission-date
      {:field/data-type             :data.type/datetime
       :field/default-ord           1550
       :field/default-visibility    {:editor           :editor.type/date
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Submission date"}}]
     [:oppo/status-and-next-steps
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           90
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Status and next steps"}}]
     [:oppo/case-note
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           50
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Deal note"}}]
     [:oppo/competetive-analysis
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1000
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Competitive analysis"}}]]
    (su/single-ref-field :oppo/fund
                         "Fund"
                         [:entity.type/fund]
                         {:field-in-use-filter      su/fund-field-in-use?
                          :option-filter-fn         option-filters/pipeline-funds
                          :default-field-visibility :in-use})

    [[:oppo/funding-referrals
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1280
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Funding referrals"}}]
     [:oppo/elevator-pitch
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1020
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Elevator pitch"}}]
     [:oppo/lead-referrer-details
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1220
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Lead referrer details"}}]
     [:oppo/lead-member
      {:field/data-type             :data.type/text-simple
       :field/default-ord           900
       :field/default-visibility    {:editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Lead member"}}]
     [:oppo/rationale
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1100
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Rationale"}}]
     [:oppo/deal-description
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           60
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Deal description"}}]
     [:oppo/current-phase-and-status
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1060
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Current phase and status"}}]
     [:oppo/ups-solving-the-problem
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1040
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "USP solving that problem"}}]
     [:oppo/link-to-pitch
      {:field/data-type             :data.type/text-url
       :field/default-ord           1570
       :field/default-visibility    {:editor           :editor.type/url
                                     :grid             :editor.type/url
                                     :filter           :filter.type/text
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Link to pitch"}}]
     [:oppo/solving-problem
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1030
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Solving what problem?"}}]
     [:oppo/strategic-fit
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           990
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Strategic fit"}}]
     [:oppo/kpi-targets-for-next-round
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1080
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "KPI targets for next round"}}]
     [:oppo/known-risks
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1250
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Known risks"}}]
     [:oppo/how-can-we-help
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1270
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "How can we help?"}}]
     ;; REFERENCES
     [:oppo/flagged-by
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           910
       :field/entity-types          [:entity.type/account]
       :field/constraints           {}
       :field/not-importable?       true
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/none
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Flagged by"}}]
     ;; INTEGERS
     [:oppo/deal-priority
      {:field/data-type             :data.type/integer
       :field/default-ord           1010
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/integer
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Deal priority"}}]
     [:oppo/number-of-shares-total-post-deal
      {:field/data-type             :data.type/integer
       :field/default-ord           930
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/integer
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Number of shares (total post deal)"}}]
     [:oppo/number-of-shares-for-us
      {:field/data-type             :data.type/integer
       :field/default-ord           920
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/integer
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Number of shares (for us)"}}]
     [:oppo/target-stake
      {:field/data-type             :data.type/integer
       :field/default-ord           1240
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/integer
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Target stake (%)"}}]
     ;; percentage
     [:oppo/number-id
      {:field/data-type             :data.type/integer
       :field/default-ord           1110
       :field/constraints           {:constraint/number-positive true}
       :field/default-visibility    {:editor           :editor.type/integer
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Deal ID"}}]
     ;; currency values
     [:oppo/cur-round-size
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1140
       :field/secondary-attribute   :oppo/cur-round-size--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Total round size"}}]
     [:oppo/cur-internal-valuation
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1290
       :field/secondary-attribute   :oppo/cur-internal-valuation--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Internal valuation (pre-money)"}}]
     [:oppo/cur-capital-sought
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1130
       :field/secondary-attribute   :oppo/cur-capital-sought--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Capital sought"}}]
     [:oppo/cur-ticket
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1580
       :field/secondary-attribute   :oppo/cur-ticket--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Ticket size"}}]
     [:oppo/cur-transaction-fees
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1190
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :oppo/cur-transaction-fees--sym
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Transaction fees"}}]
     [:oppo/nd-per-ebitda
      {:field/data-type             :data.type/number
       :field/default-ord           940
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "ND/EBITDA"}}]
     [:oppo/ev-per-ebitda
      {:field/data-type             :data.type/number
       :field/default-ord           950
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "EV/EBITDA"}}]
     [:oppo/cur-enterprise-value
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1180
       :field/secondary-attribute   :oppo/cur-enterprise-value--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Enterprise value (EV)"}}]
     [:oppo/cur-post-money
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1230
       :field/secondary-attribute   :oppo/cur-post-money--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Post-money valuation"}}]
     [:oppo/cur-pre-money
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1150
       :field/secondary-attribute   :oppo/cur-pre-money--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Pre-money"}}]
     [:oppo/cur-equity-value
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1170
       :field/secondary-attribute   :oppo/cur-equity-value--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Equity Value"}}]
     [:oppo/cur-net-debt
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1160
       :field/secondary-attribute   :oppo/cur-net-debt--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "ND (Net debt)"}}]
     ;; currency symbols
     [:oppo/cur-enterprise-value--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-enterprise-value
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Enterprise value (EV) (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-transaction-fees--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-transaction-fees
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Transaction fees (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-internal-valuation--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-internal-valuation
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Internal valuation (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-pre-money--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-pre-money
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Pre-money (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-equity-value--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-equity-value
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Equity value (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-net-debt--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-net-debt
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "ND (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-post-money--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-post-money
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Post-money valuation (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-capital-sought--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-capital-sought
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Capital sought (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-ticket--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-ticket
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Ticket size (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     [:oppo/cur-round-size--sym
      {:field/data-type             :data.type/currency-symbol
       :value-type                  :keyword
       :field/related-fields        :oppo/cur-round-size
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Total round size (currency)"}
       :field/secondary-field?      true
       :field/not-clearable?        true
       :options                     currencies/options}]
     ;; entity relationships
     ;; NOTE! :oppo/funding-round-v2 target entity type has been changed from :entity.type/funding-round to :entity.type/named-value on 1/2020

     [:oppo/funding-round-v2
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1200
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/funding-round-v2
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Funding round"}}]

     ;; NOTE! :oppo/lead-source-v2 target entity type has been changed from :entity.type/lead-source to :entity.type/named-value on 1/2020

     [:oppo/lead-source-v2
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1120
       :value-type                  :entity
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/lead-source-v2
       :create-on-fly?              true
       :field/not-clearable?        true
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Source category"}}]

     ;; NOTE! :oppo/detailed-lead-source target entity type has been changed from :entity.type/detailed-lead-source to :entity.type/named-value on 1/2020
     [:oppo/detailed-lead-source
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1300
       :value-type                  :entity
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/detailed-lead-source
       :create-on-fly?              true
       :field/not-clearable?        true
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Source details"}}]
     [:oppo/owner owner-field]
     common-schema/team-members-field
     common-schema/common-network-drive-field
     [:oppo/credit
      {:field/data-type             :data.type/ref-single
       :field/default-ord           900
       :value-type                  :entity
       :field/entity-types          [:entity.type/account]
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/user}
       :field/translations          {:en "Credit for this lead"}
       :field/option-filter-fn      option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                      {"extras.active-user" true})}]
     [:oppo/stage
      {:field/data-type             :data.type/ref-single
       :field/default-ord           70
       :field/not-clearable?        true
       :value-type                  :entity
       :field/mandatory             :mandatory/always
       :field/system-semantics      su/user-provided-system-required-field-semantics
       :field/system-required?      true
       :field/entity-types          [:entity.type/stage]
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Stage"}
       :field/option-filter-fn      option-filters/stages-in-pipeline}]

     ;; NOTE! :oppo/lead-type (:data.type/keyword) has been updated to oppo/lead-type-v2 (:data.type/ref-field) on 8/2020

     [:oppo/lead-type-v2
      {:field/data-type             :data.type/ref-single
       :field/default-ord           890
       :value-type                  :entity
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/lead-type-v2
       :create-on-fly?              true
       :field/not-clearable?        true
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/user}
       :field/translations          {:en "Lead type"}}]
     ;; options
     [:oppo/status
      {:field/data-type                  :data.type/keyword
       :field/default-ord                80
       :value-type                       :keyword
       :field/mandatory                  :mandatory/always
       :field/system-semantics           su/user-provided-system-required-field-semantics
       :field/system-required?           true
       :field/default-visibility         {:field-visibility :in-use-disabled
                                          :editor           :editor.type/none
                                          :grid             :editor.type/select
                                          :filter           :filter.type/select}
       :field/default-authorization      {:access/read  :role/guest-buy-side
                                          :access/write :role/user}
       :field/not-clearable?             true
       :field/hidden-from-configuration? true
       :field/translations               {:en "Status"}
       :options                          statuses/oppo-status-options}]
     ;; system fields
     [:oppo/moved-to-portfolio-by
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1370
       :value-type                  :entity
       :field/entity-types          [:entity.type/account]
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Moved to portfolio by"}
       :field/not-importable?       true
       :field/option-filter-fn      option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                      {"extras.active-user" true})}]
     [:oppo/passed-by
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1390
       :value-type                  :entity
       :field/entity-types          [:entity.type/account]
       :field/hide-from-webform?    true
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Passed by"}
       :field/not-importable?       true
       :field/option-filter-fn      option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                      {"extras.active-user" true})}]
     [:oppo/put-on-hold-by
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1420
       :value-type                  :entity
       :field/hide-from-webform?    true
       :field/entity-types          [:entity.type/account]
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Put on hold by"}
       :field/not-importable?       true
       :field/option-filter-fn      option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                      {"extras.active-user" true})}]
     [:oppo/reactivated-by
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1440
       :field/hide-from-webform?    true
       :value-type                  :entity
       :field/entity-types          [:entity.type/account]
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Reactivated by"}
       :field/not-importable?       true
       :field/option-filter-fn      option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as _args}]
                                      {"extras.active-user" true})}]
     [:oppo/last-pass-comment
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1350
       :field/hide-from-webform?    true
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Last pass comment"}}]
     [:oppo/status-comment
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1310
       :field/hide-from-webform?    true
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Status update comment"}
       :field/not-importable?       true}]
     [:oppo/last-hold-comment
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1340
       :field/hide-from-webform?    true
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/user}
       :field/translations          {:en "Last hold comment"}}]]

    ;; NOTE! :oppo/pass-reason-v2 target entity type has been changed from :entity.type/pass-reason to :entity.type/named-value on 3/2022

    (su/option-field :oppo/pass-reason-v2
                     "Pass reason"
                     {:default-field-visibility :in-use-disabled
                      :authorization            su/access-user-ssg
                      :hide-from-webform?       true})
    (su/option-field :oppo/pass-reason-secondary
                     "Secondary pass reason"
                     {:default-field-visibility :not-in-use
                      :authorization            su/access-user-ssg
                      :hide-from-webform?       true})
    [[:oppo/target-asset
      {:field/data-type                  :data.type/ref-single
       :field/default-ord                20
       :create-on-fly?                   true
       :field/entity-types               [:entity.type/asset]
       :field/deep-indexing?             true
       :field/select-target-title        {:en "Select target asset"}
       :field/select-target-label        {:en "Target asset"}
       :value-type                       :entity
       :field/default-visibility         {:editor           :editor.type/link
                                          :grid             :editor.type/link
                                          :filter           :filter.type/select
                                          :field-visibility :in-use-disabled}
       :field/hidden-from-configuration? true
       :field/default-authorization      {:access/read  :role/guest-buy-side
                                          :access/write :role/user}
       :field/translations               {:en "Target asset"}}]
     ;; DATES
     ;; The :mandatory/partially date fields below are workflow-state metadata:
     ;; they drive import-template "^" markers and state-specific UX, but they
     ;; must not become target system-required nullability.
     [:oppo/reactivated-on
      {:field/data-type             :data.type/datetime
       :field/default-ord           1430
       :field/constraints           {}
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Reactivated on"}
       :field/not-importable?       true}]
     [:oppo/on-hold-until
      {:field/data-type             :data.type/datetime
       :field/default-ord           1400
       :field/mandatory             :mandatory/partially
       :field/hide-from-webform?    true
       :field/constraints           {}
       :field/default-visibility    {:editor           :editor.type/date
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/user}
       :field/translations          {:en "On hold until"}
       :field/custom-params         (fn [oppo]
                                      (merge
                                       {:min-date (+ (ct/now-millis)
                                                     (* 2 24 3600 1000))}
                                       (when (= :oppo.status/on-hold (:oppo/status oppo))
                                         {:field/not-clearable? true})))}]
     [:oppo/moved-to-portfolio-on
      {:field/data-type             :data.type/datetime
       :field/default-ord           1360
       :field/constraints           {}
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Moved to portfolio on"}
       :field/not-importable?       true}]
     [:oppo/passed-on
      {:field/data-type             :data.type/datetime
       :field/default-ord           1380
       :field/mandatory             :mandatory/partially
       :field/hide-from-webform?    true
       :field/constraints           {}
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Passed on"}}]
     [:oppo/put-on-hold-on
      {:field/data-type             :data.type/datetime
       :field/default-ord           1410
       :field/constraints           {}
       :field/mandatory             :mandatory/partially
       :field/hide-from-webform?    true
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Put on hold on"}}]
     [:oppo/ratings
      {:field/data-type             :data.type/text-edn
       :field/default-ord           1610
       :field/constraints           {}
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/none
                                     :filter           :filter.type/none
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Ratings"}
       :field/not-importable?       true}]
     [:oppo/group-companies
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           1470
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/group-companies
       :field/default-visibility    {:editor           :editor.type/multi-select
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Group companies"}}]
     [:oppo/stake-type
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           1480
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/stake-type
       :field/default-visibility    {:editor           :editor.type/multi-select
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Stake type"}}]
     [:oppo/lead-channel
      {:field/data-type             :data.type/text-simple
       :field/default-ord           1510
       :field/default-visibility    {:editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/translations          {:en "Lead channel"}}]
     [:oppo/group-business-units
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           1520
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/group-business-units
       :field/default-visibility    {:editor           :editor.type/multi-select
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Group business units"}}]
     [:oppo/group-introduction-comments
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1530
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Group introduction comments"}}]
     [:oppo/cvf-team-comment
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Comment from CVF Team"}}]
     [:oppo/value-creation-business-development
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Value creation - Business development"}}]
     [:oppo/value-creation-cluster-effect
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Value creation - Cluster effect"}}]
     [:oppo/value-creation-corporate-governance
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Value creation - Corporate Governance"}}]
     [:oppo/value-creation-fundraising
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Value creation - Fundraising"}}]
     [:oppo/value-creation-government-and-regulation
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Value creation - Government & Regulation"}}]
     [:oppo/value-creation-recruitment
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1531
       :field/default-visibility    {:editor           :editor.type/textarea
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations          {:en "Value creation - Recruitment"}}]
     [:oppo/asset-class
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1490
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/asset-class
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Asset class"}}]
     [:oppo/program
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1500
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/program
       :field/default-visibility    {:editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Program"}}]
     [:oppo/hkstp-program
      {:field/data-type             :data.type/ref-single
       :field/default-ord           1500
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/hkstp-program
       :field/default-visibility    {:field-visibility :not-in-use-blocked
                                     :editor           :editor.type/select
                                     :grid             :editor.type/select
                                     :filter           :filter.type/select}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "HKSTP program"}}]
     [:oppo/tag-aandeel
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           100
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-aandeel
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Aandeel"}}]
     [:oppo/tag-application
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           110
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-application
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Application"}}]
     [:oppo/tag-business
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           120
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-business
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Business"}}]
     [:oppo/tag-business-model
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           130
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-business-model
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Business model"}}]
     [:oppo/tag-business-unit
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           140
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-business-unit
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Business unit"}}]
     [:oppo/tag-co-development-routing
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           150
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-co-development-routing
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Co-development routing"}}]
     [:oppo/tag-company-stage
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           160
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-company-stage
       :field/default-visibility    {:editor :editor.type/none
                                     :grid   :editor.type/multi-select
                                     :filter :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Company stage"}}]
     [:oppo/tag-country
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           170
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-country
       :field/default-visibility    {:editor           :editor.type/multi-select
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Country"}}]
     [:oppo/tag-crc-center
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           180
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-crc-center
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "CRC Center"}}]
     [:oppo/tag-deal-category
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           190
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-deal-category
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Deal category"}}]
     [:oppo/tag-deal-industry
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           200
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-deal-industry
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Deal industry"}}]
     [:oppo/tag-deal-revenue-and-transaction-tags
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           210
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-deal-revenue-and-transaction-tags
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Deal revenue and transaction tags"}}]
     [:oppo/tag-deal-tags-investing-entity-and-operator
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           220
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-deal-tags-investing-entity-and-operator
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Deal tags (Investing entity and Operator)"}}]
     [:oppo/tag-deal-type
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           230
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-deal-type
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Deal type"}}]
     [:oppo/tag-disease-area
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           240
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-disease-area
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Disease Area"}}]
     [:oppo/tag-distributed-teams
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           250
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-distributed-teams
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Distributed teams"}}]
     [:oppo/tag-engagement-type
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           260
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-engagement-type
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Engagement type"}}]
     [:oppo/tag-fintech-sub-sector
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           270
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-fintech-sub-sector
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "FinTech sub-sector"}}]
     [:oppo/tag-focus-area
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           280
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-focus-area
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Focus area"}}]
     [:oppo/tag-fortum-business-area
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           290
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-fortum-business-area
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Fortum business area"}}]
     [:oppo/tag-functions
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           300
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-functions
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Functions"}}]
     [:oppo/tag-fund
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           310
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-fund
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Fund"}}]
     [:oppo/tag-geography
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           320
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-geography
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Geography"}}]
     [:oppo/tag-geography-new
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           330
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-geography-new
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "GeographyNEW#"}}]

     [:oppo/tag-goal
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           340
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-goal
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Goal"}}]
     [:oppo/tag-hot-tags
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           350
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-hot-tags
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Hot tags"}}]
     [:oppo/tag-if-stages
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           360
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-if-stages
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "IG stages"}}]
     [:oppo/tag-industrie-start-up
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           370
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-industrie-start-up
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Industrie - Start-Up"}}]

     [:oppo/tag-industry
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           380
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-industry
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Industry"}}]
     [:oppo/tag-industry-new
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           390
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-industry-new
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "IndustryNEW#"}}]
     [:oppo/tag-industry-segment
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           400
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-industry-segment
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Industry segment"}}]

     [:oppo/tag-investing-entity
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           410
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-investing-entity
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Investing entity"}}]

     [:oppo/tag-investment-theme
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           420
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-investment-theme
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Investment theme"}}]

     [:oppo/tag-investment-thesis
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           430
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-investment-thesis
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Investment thesis"}}]

     [:oppo/tag-investment-type
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           440
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-investment-type
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Investment type"}}]

     [:oppo/tag-kaercher-stakeholder-and-geography
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           450
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-kaercher-stakeholder-and-geography
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Kärcher Stakeholder & Geography"}}]

     [:oppo/tag-keywords
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           460
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-keywords
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Keywords"}}]
     [:oppo/tag-lage
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           470
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-lage
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Lage"}}]
     [:oppo/tag-lifestage
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           480
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-lifestage
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "LifeStage"}}]
     [:oppo/tag-location
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           490
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-location
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Location"}}]
     [:oppo/tag-nda
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           500
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-nda
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "NDA"}}]
     [:oppo/tag-onda
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           510
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-onda
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Onda"}}]
     [:oppo/tag-operator
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           520
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-operator
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Operator"}}]
     [:oppo/tag-phase
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           530
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-phase
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Phase"}}]
     [:oppo/tag-potential-fund-investment
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           540
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-potential-fund-investment
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Potential fund investment"}}]

     [:oppo/tag-primary-tag
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           550
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-primary-tag
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Primary tag"}}]

     [:oppo/tag-rationale
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           560
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-rationale
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Rationale"}}]
     [:oppo/tag-real-estate-sectors
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           570
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-real-estate-sectors
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Real estate sectors"}}]

     [:oppo/tag-region
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           580
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-region
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Region"}}]

     [:oppo/tag-relevance-for-aic
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           590
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-relevance-for-aic
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Relevance for AIC"}}]

     [:oppo/tag-secondary-tag
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           600
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-secondary-tag
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Secondary tag"}}]

     [:oppo/tag-sector
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           610
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-sector
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Sector"}}]

     [:oppo/tag-smoke
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           620
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-smoke
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Smoke"}}]
     [:oppo/tag-smoke-1
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           630
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-smoke-1
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Smoke_1"}}]

     [:oppo/tag-smoke-2
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           640
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-smoke-2
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Smoke_2"}}]

     [:oppo/tag-solution
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           650
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-solution
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Solution"}}]
     [:oppo/tag-source
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           660
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-source
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Source"}}]

     [:oppo/tag-species
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           670
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-species
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Species"}}]

     [:oppo/tag-sponsored-by
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           680
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-sponsored-by
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Sponsored by"}}]
     [:oppo/tag-stage
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           690
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-stage
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Stage"}}]
     [:oppo/tag-startup-format
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           700
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-startup-format
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Startup format"}}]
     [:oppo/tag-strategic-focus-area
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           710
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-strategic-focus-area
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Strategic focus area"}}]

     [:oppo/tag-strategy-alignment
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           720
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-strategy-alignment
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Strategy alignment"}}]

     [:oppo/tag-sub-industry
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           730
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-sub-industry
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Sub-industry"}}]
     [:oppo/tag-subverticals
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           740
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-subverticals
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Subverticals"}}]
     [:oppo/tag-supply-chain
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           750
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-supply-chain
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Supply chain"}}]
     [:oppo/tag-target-sectors
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           760
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-target-sectors
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Target sectors"}}]
     [:oppo/tag-technologies
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           770
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-technologies
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Technologies"}}]
     [:oppo/tag-technology-and-business-model
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           780
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-technology-and-business-model
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Technology & Business Model"}}]
     [:oppo/tag-technology-topic
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           790
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-technology-topic
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Technology / Topic"}}]
     [:oppo/tag-themes
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           800
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-themes
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Themes"}}]
     [:oppo/tag-tvc-dd-funnel
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           810
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-tvc-dd-funnel
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "TVC DD funnel (= already done)"}}]
     [:oppo/tag-type-of-investment
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           820
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-type-of-investment
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Type of investment"}}]
     [:oppo/tag-type-of-technology
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           830
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-type-of-technology
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Type of technology"}}]
     [:oppo/tag-vertical-subvertical
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           840
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-vertical-subvertical
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Vertical/Subvertical"}}]
     [:oppo/tag-verticals
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           850
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-verticals
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Verticals"}}]
     [:oppo/tag-dash
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           860
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-dash
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "-"}}]
     [:oppo/tag-hitachi-project
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           865
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-hitachi-project
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Hitachi Project"}}]
     ;; The index holds the start of an age, not the days: see
     ;; `common.schema.entities.oppo.time-fields`.
     [:oppo/active-since
      {:synthetic?                  true
       :field/calculated-field      time-fields/active-since
       :field/default-ord           1321
       :field/data-type             :data.type/datetime
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :not-in-use-blocked}
       :field/hidden-from-configuration? true
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "Active since"}}]
     [:oppo/on-hold-since
      {:synthetic?                  true
       :field/calculated-field      time-fields/on-hold-since
       :field/default-ord           1331
       :field/data-type             :data.type/datetime
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/date
                                     :filter           :filter.type/date
                                     :field-visibility :not-in-use-blocked}
       :field/hidden-from-configuration? true
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "On hold since"}}]
     [:oppo/age-in-days-active
      {:synthetic?                  true
       :field/days-since-attribute  :oppo/active-since
       :field/default-ord           1320
       :field/data-type             :data.type/integer
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "Age in days (active)"}}]
     [:oppo/age-in-days-on-hold
      {:synthetic?                  true
       :field/days-since-attribute  :oppo/on-hold-since
       :field/default-ord           1330
       :field/data-type             :data.type/integer
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "Age in days (on hold)"}}]
     [:oppo/overview-competition
      {:synthetic?                  true
       :field/data-type             :data.type/text-multiline
       :field/default-ord           1620
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/textarea
                                     :filter           :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "Competition"}}]
     [:oppo/throughput-in-weeks-invested
      {:synthetic?                  true
       :field/calculated-field      time-fields/throughput-in-weeks-invested
       ;; A sum of the weeks of many deals means nothing: the total is the average.
       :field/total-kind            :avg
       :field/default-ord           1450
       :field/data-type             :data.type/integer
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "Throughput in weeks (invested)"}}]
     [:oppo/throughput-in-weeks-passed
      {:synthetic?                  true
       :field/calculated-field      time-fields/throughput-in-weeks-passed
       :field/total-kind            :avg
       :field/default-ord           1460
       :field/data-type             :data.type/integer
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/integer
                                     :filter           :filter.type/integer
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/translations          {:en "Throughput in weeks (passed)"}}]]
    (su/currency-field :oppo/cur-price-indication "Price indication" {})
    (su/currency-field :oppo/cur-expected-deal-value
                       "Expected deal value"
                       {:authorization su/access-bsg-bsg})
    (su/percentage-field :oppo/yield-indication-percentage
                         "Yield indication %"
                         {:authorization su/access-bsg-bsg})
    (su/currency-field :oppo/cur-equity-needed "Equity needed" {:authorization su/access-bsg-bsg})
    (su/percentage-field :oppo/ltc "LTC (loan to cost)" {:authorization su/access-bsg-bsg})
    (su/currency-field :oppo/cur-broken-deal-expenses
                       "Broken-deal expenses"
                       {:default-field-visibility :in-use})
    (su/text-simple-field :oppo/broker "Broker" {:authorization su/access-bsg-bsg})
    [[:oppo/cur-price-per-sqm
      {:field/data-type             :data.type/currency-value
       :field/secondary-attribute   :oppo/cur-price-indication--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:oppo/target-asset
                                     :real-estate/property-area]
       :field/calculated-field      (fn [{:keys [entity-data entity-lookup tr bigdec->float]}]
                                      (let [price (bigdec->float (:oppo/cur-price-indication
                                                                  entity-data))
                                            re (entity-lookup (:oppo/target-asset entity-data))
                                            area (:real-estate/property-area re)]
                                        (cond (not (number? price))
                                              (tr :not-a-number
                                                  [(tr :oppo/cur-price-indication)])
                                              (not (number? area))
                                              (tr :not-a-number
                                                  [(tr :real-estate/property-area)])
                                              (zero? area)
                                              (tr :zero-value-error
                                                  [(tr :real-estate/property-area)])
                                              :else
                                              (/ price area))))
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/translations          {:en "Price per sqm (㎡)"}}]]
    [[:oppo/cur-price-per-RE-assets-sqm
      {:field/data-type             :data.type/currency-value
       :field/secondary-attribute   :oppo/cur-price-indication--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor           :editor.type/number
                                     :grid             :editor.type/number
                                     :filter           :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:oppo/candidate-target-real-estates
                                     :real-estate/lettable-area]
       ;; The field reads the area of the real estates, so a change of one of them
       ;; builds the document of the deal again.
       :calculation-triggers        {:entity.type/real-estate [[:oppo/_candidate-target-real-estates]]}
       :field/calculated-field      (fn [{:keys [entity-data entity-lookup tr bigdec->float]}]
                                      (let [price (bigdec->float (:oppo/cur-price-indication
                                                                  entity-data))
                                            re-assets (->> entity-data
                                                           :oppo/candidate-target-real-estates
                                                           (keep entity-lookup)
                                                           (remove :common/disabled?)
                                                           (doall))
                                            area (->> re-assets
                                                      (map :real-estate/lettable-area)
                                                      (filter number?)
                                                      #?(:cljs (remove (fn [v]
                                                                         (js/isNaN v))))
                                                      (apply +))]
                                        (cond
                                          (not (number? price))
                                          (tr :not-a-number [(tr :oppo/cur-price-indication)])
                                          (not (number? area))
                                          (tr :not-a-number [(tr :real-estate/lettable-area)])
                                          (zero? area)
                                          (tr :zero-value-error
                                              [(tr :real-estate/lettable-area)])
                                          :else
                                          (/ price area))))
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/translations          {:en "Price per total sqm (㎡)"}}]]

    (su/option-field :oppo/source-category-re
                     "Source category (RE)"
                     {:authorization su/access-bsg-bsg})

    (su/option-field :oppo/vake-primary-indirect-impact
                     "Primary indirect impact"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}})

    (su/option-field :oppo/vake-secondary-indirect-impact
                     "Secondary indirect impact"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}})

    (su/option-field :oppo/vake-direct-employment-impact
                     "Direct employment impact"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}
                      :export-type              :data.type/integer})

    (su/option-field :oppo/vake-eu-funding-potential
                     "EU funding potential"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}
                      :export-type              :data.type/integer})

    (su/option-field-multi :oppo/vake-key-market-deficiency
                           "Key market deficiency"
                           {:default-field-visibility :not-in-use-blocked
                            :authorization            {:access/read   :role/guest-buy-side
                                                       :access/create :role/admin
                                                       :access/write  :role/guest-buy-side}})

    (su/option-field :oppo/vake-preparatory-project-for-an-investment-case
                     "Preparatory project for an investment case"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}})

    (su/option-field :oppo/vake-emission-reduction-potential
                     "Emission reduction potential"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}
                      :export-type              :data.type/integer})

    (su/option-field :oppo/vake-min-roi
                     "Min ROI"
                     {:default-field-visibility :not-in-use-blocked
                      :authorization            {:access/read   :role/guest-buy-side
                                                 :access/create :role/admin
                                                 :access/write  :role/guest-buy-side}})

    (su/option-field-multi :oppo/exceptional-terms
                           "Exceptional terms"
                           {:authorization {:access/read   :role/guest-buy-side
                                            :access/create :role/admin
                                            :access/write  :role/guest-buy-side}})

    (su/text-multiline-field :oppo/exceptional-terms-comment
                             "Exceptional terms, comment"
                             {:authorization su/access-bsg-bsg})

    [[:oppo/tag-owner-round
      {:field/data-type             :data.type/ref-multi
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-owner-round
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Owner round"}}]

     [:oppo/tag-innovation-council
      {:field/data-type             :data.type/ref-multi
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-innovation-council
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Innovation council"}}]]
    (su/multi-ref-field :oppo/candidate-target-real-estates
                        "RE target(s)"
                        [:entity.type/real-estate]
                        {:default-field-visibility :not-in-use-blocked
                         :editor-type              :editor.type/grid})
    (su/currency-field :oppo/cur-cheque-size "Cheque size" {:authorization su/access-bsg-bsg})
    [[:oppo/tag-project
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           700
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-project
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Project"}}]]
    (su/text-multiline-field :oppo/impact "Impact" {:authorization su/access-bsg-bsg})
    (su/text-simple-field :oppo/payback-time
                          "Expected payback time (yrs)"
                          {:authorization su/access-bsg-bsg})
    (su/integer-field :oppo/number-of-licenses
                      "Number of licenses"
                      {:authorization su/access-bsg-bsg})
    (su/integer-field :oppo/number-of-female-founders
                      "Number of female founders"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use-blocked})
    (su/integer-field :oppo/number-of-male-founders
                      "Number of male founders"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/datetime-alj-deadline-to-close-deal
                   "Deadline to close deal"
                   {:default-field-visibility :not-in-use-blocked
                    :authorization            su/access-bsg-bsg})
    (su/date-field :oppo/datetime-alj-deadline-to-complete-full-dd
                   "Deadline to complete full DD"
                   {:default-field-visibility :not-in-use-blocked
                    :authorization            su/access-bsg-bsg})
    (su/date-field :oppo/datetime-alj-deadline-to-complete-preliminary-dd
                   "Deadline to complete preliminary DD"
                   {:default-field-visibility :not-in-use-blocked
                    :authorization            su/access-bsg-bsg})
    (su/date-field :oppo/datetime-alj-deadline-to-present-to-ic
                   "Deadline to present to IC"
                   {:default-field-visibility :not-in-use-blocked
                    :authorization            su/access-bsg-bsg})
    (su/text-multiline-field :oppo/distribution-opportunity
                             "Distribution opportunity"
                             {:authorization su/access-bsg-bsg})
    (su/percentage-field :oppo/expected-irr "Expected IRR" {:authorization su/access-bsg-bsg})
    (su/text-simple-field :oppo/deal-synergies "Deal synergies" {:authorization su/access-bsg-bsg})
    (su/text-simple-field :oppo/esg-impact "ESG impact" {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/bu-lead "BU lead" {:authorization su/access-bsg-bsg})
    [[:oppo/tag-esg-environmental-impact
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           701
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-esg-environmental-impact
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Environmental impact"}}]
     [:oppo/tag-esg-governance-impact
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           702
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-esg-governance-impact
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Governance impact"}}]
     [:oppo/tag-esg-social-impact
      {:field/data-type             :data.type/ref-multi
       :field/default-ord           703
       :value-type                  :entity
       :create-on-fly?              true
       :field/entity-types          [:entity.type/named-value]
       :named-value-attribute       :oppo/tag-esg-social-impact
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/multi-select
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-buy-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-buy-side}
       :field/translations          {:en "Social impact"}}]]

    (su/date-field :oppo/date-IC-presentation "IC presentation" {:authorization su/access-bsg-bsg})
    (su/date-field :oppo/date-CIC-presentation
                   "CIC presentation"
                   {:default-field-visibility :not-in-use-blocked
                    :authorization            su/access-bsg-bsg})
    (su/text-multiline-field :oppo/CIC-notes
                             "CIC notes"
                             {:default-field-visibility :not-in-use-blocked
                              :authorization            su/access-bsg-bsg})
    (su/text-multiline-field :oppo/deal-terms "Deal terms" {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/distribution-terms
                             "Distribution terms"
                             {:default-field-visibility :not-in-use-blocked
                              :authorization            su/access-bsg-bsg})
    (su/percentage-field :oppo/net-margin
                         "Net margin"
                         {:default-field-visibility :not-in-use-blocked
                          :authorization            su/access-bsg-bsg})
    (su/percentage-field :oppo/debt-to-capital
                         "Debt to capital"
                         {:default-field-visibility :not-in-use-blocked
                          :authorization            su/access-bsg-bsg})
    (su/option-field-multi :oppo/tag-project-type "Project type" {:authorization su/access-bsg-bsg})
    (su/option-field-multi :oppo/co-contributor "Co-contributor" {:authorization su/access-bsg-bsg})
    (su/currency-field :oppo/cur-total-project-investment
                       "Total project investment"
                       {:default-field-visibility :not-in-use-blocked
                        :authorization            su/access-bsg-bsg})
    (su/option-field-multi :oppo/tag-segment "Segment" {:authorization su/access-bsg-bsg})
    (su/text-multiline-field :oppo/ic-notes "IC notes" {:authorization su/access-bsg-bsg})

    (su/date-field :oppo/date-project-completion
                   "Project completion date"
                   {:default-field-visibility :not-in-use-blocked
                    :authorization            su/access-bsg-bsg})
    (su/text-simple-field :oppo/financing-mix "Financing mix" {:authorization su/access-bsg-bsg})
    (su/text-simple-field :oppo/partner "Partner" {:authorization su/access-bsg-bsg})
    (su/percentage-field :oppo/target-stake-percentage
                         "Target stake"
                         {:authorization su/access-bsg-bsg})
    (su/keyword-field :oppo/country "Country" country/options {:authorization su/access-bsg-bsg})
    (su/keyword-multi-field :oppo/countries
                            "Countries"
                            country/options
                            {:authorization su/access-bsg-bsg})
    (su/keyword-multi-field :oppo/target-countries
                            "Target countries"
                            country/options
                            {:authorization su/access-bsg-bsg})
    (su/option-field :oppo/region "Region" {:authorization su/access-bsg-bsg})
    (su/option-field-multi :oppo/regions "Regions" {:authorization su/access-bsg-bsg})
    (su/currency-field :oppo/cur-funds-raised-to-date
                       "Funds raised to date"
                       {:default-field-visibility :not-in-use
                        :authorization            su/access-bsg-bsg})
    (su/date-field :oppo/date-psd-update
                   "Date of PSD update"
                   {:default-field-visibility :not-in-use
                    :authorization            su/access-bsg-bsg})
    (su/datetime-field :oppo/datetime-deal-initial-review-date
                       "Deal initial review date"
                       {:default-field-visibility :not-in-use
                        :authorization            su/access-bsg-bsg})
    (su/datetime-field :oppo/datetime-deal-first-meeting-date
                       "Deal 1st meeting date"
                       {:default-field-visibility :not-in-use
                        :authorization            su/access-bsg-bsg})
    (su/integer-field :oppo/deal-year
                      "Deal year"
                      {:default-field-visibility :not-in-use
                       :authorization            su/access-bsg-bsg})

    (su/currency-field :oppo/cur-total-round-size-equity
                       "Total round size (equity)"
                       {:default-field-visibility :not-in-use
                        :authorization            su/access-bsg-bsg})
    (su/currency-field :oppo/cur-debt-financing-for-round
                       "Debt financing for the round"
                       {:default-field-visibility :not-in-use
                        :authorization            su/access-bsg-bsg})
    (su/option-field :oppo/investment-stage
                     "Investment stage"
                     {:default-field-visibility :not-in-use
                      :authorization            su/access-bsg-bsg})
    (su/currency-field :oppo/cur-ebitda
                       "EBITDA"
                       {:default-field-visibility :not-in-use-blocked
                        :authorization            su/access-bsg-bsg})
    (su/option-field-multi :oppo/due-diligence-scope
                           "Due diligence scope"
                           {:default-field-visibility :not-in-use
                            :authorization            su/access-bsg-bsg})
    (su/date-field :oppo/date-of-investment-decision
                   "Investment decision date"
                   {:default-field-visibility :not-in-use
                    :authorization            su/access-bsg-bsg})
    (su/currency-field :oppo/cur-investment-decision-amount
                       "Investment decision amount"
                       {:default-field-visibility :not-in-use
                        :authorization            su/access-bsg-bsg})
    (su/date-field :oppo/date-of-closing
                   "Closing date"
                   {:default-field-visibility :not-in-use
                    :authorization            su/access-bsg-bsg})
    (su/currency-field :oppo/cur-alj-investment
                       "ALJ Investment"
                       {:default-field-visibility :not-in-use-blocked
                        :authorization            su/access-bsg-bsg})
    (su/percentage-field :oppo/expected-net-irr
                         "Expected net IRR"
                         {:default-field-visibility :not-in-use
                          :authorization            su/access-bsg-bsg})
    (su/currency-field :oppo/cur-target-size
                       "Target size"
                       {:default-field-visibility :not-in-use-blocked
                        :authorization            su/access-bsg-bsg})
    (su/option-field :oppo/CIC-recommendation
                     "CIC-recommendation"
                     {:default-field-visibility :not-in-use-blocked})
    (su/option-field :oppo/IC-recommendation "IC-recommendation" {})
    (su/option-field :oppo/met-management
                     "Met management"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})
    (su/option-field-multi :oppo/tag-deal-status
                           "Deal status"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use})
    (su/option-field :oppo/angle
                     "Angle"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})

    [[:oppo/alj-SBDI-lead
      (-> owner-field
          (potpuri/deep-merge
           {:field/default-visibility    {:field-visibility :not-in-use-blocked}
            :field/system-required?      false
            :field/composite-attributes  [:oppo/owner]
            :field/calculated-field      (fn [{:keys [entity-data]}]
                                           (get entity-data :oppo/owner))
            :field/default-authorization {:access/read  :role/guest-buy-side
                                          :access/write :role/system}
            :field/translations          {:en "SBDI lead"}})
          ;; owner-field carries :field/system-semantics; this field is
          ;; calculated from :oppo/owner and has none of its own. deep-merge
          ;; cannot express that - merging nil leaves the key present with a
          ;; nil value, and the closed field schema permits the key being
          ;; ABSENT ({:optional true}), not nil - so drop it after merging.
          (dissoc :field/system-semantics))]]
    (su/percentage-field :oppo/jimco-rating
                         "JIMCO Rating"
                         {:authorization            su/access-bsg-bsg
                          :default-field-visibility :not-in-use-blocked})
    (su/option-field :oppo/commercialization-potential
                     "Commercialization potential"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})
    (su/text-multiline-field :oppo/commercialization-plans
                             "Commercialization plans"
                             {:authorization            su/access-bsg-bsg
                              :default-field-visibility :not-in-use})
    (su/url-field :oppo/website
                  "Website"
                  {:authorization            su/access-bsg-bsg
                   :default-field-visibility :not-in-use})
    (su/single-ref-field :oppo/investor-source
                         "Investor source"
                         [:entity.type/investor]
                         {:authorization            su/access-user-user
                          :create-on-fly?           true
                          :select-entity-types [:entity.type/person :entity.type/company]
                          :create-entity-types [:entity.type/person :entity.type/company]
                          :default-field-visibility :not-in-use})
    (su/option-field-multi :oppo/regulatory-class
                           "Regulatory class"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use})
    (su/option-field-multi :oppo/therapeutic-area
                           "Therapeutic area"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use})
    (su/option-field :oppo/tesi-tier-probability
                     "Tesi tier (probability)"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :oppo/tesi-krr-tier
                     "KRR tier (probability)"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :oppo/tesi-krr
                     "KRR"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-tesi-deal-introduction-team
                   "Deal introduction - team"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-tesi-deal-introduction-mngmt
                   "Deal introduction - mngmt"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-tesi-dd-summary
                   "DD summary - team"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-tesi-investment-presentation-mngmnt
                   "Investment presentation - mngmt"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})

    (su/date-field :oppo/date-tesi-board-decision
                   "Board decision"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})

    (su/date-field :oppo/date-tesi-dry-closing
                   "Dry closing"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})

    (su/date-field :oppo/date-tesi-first-closing
                   "First closing"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})

    (su/date-field :oppo/date-tesi-final-closing
                   "Final closing"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})

    (su/integer-field :oppo/decision
                      "Estimated decision (year)"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use-blocked})

    (su/currency-field :oppo/cur-tesi-commitment
                       "Tesi commitment"
                       {:authorization            su/access-bsg-bsg
                        :default-field-visibility :not-in-use-blocked})

    (su/currency-field :oppo/cur-tesi-krr-commitment
                       "KRR commitment"
                       {:authorization            su/access-bsg-bsg
                        :default-field-visibility :not-in-use-blocked})

    [[:oppo/calc-oldest-introducer-firm
      {:field/data-type             :data.type/ref-single
       :value-type                  :entity
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/link
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/entity-types          [:entity.type/company]
       :field/composite-attributes  [:common/related-entities
                                     :entity-association/source-entity
                                     :entity-association/target-entity]
       :calculation-triggers        (oldest-association-triggers :entity.type/company)
       :field/calculated-field
       (fn
         [{:keys [entity-data entity-lookup
                  reverse-lookup]}]
         (let [source-associations (reverse-lookup
                                    {:entity                 entity-data
                                     :attribute-entity-types [:entity.type/entity-association]
                                     :attribute              :entity-association/source-entity})
               target-associations (reverse-lookup
                                    {:entity                 entity-data
                                     :attribute-entity-types [:entity.type/entity-association]
                                     :attribute              :entity-association/target-entity})
               related-associations (concat source-associations
                                            target-associations)]
           (->> related-associations
                (keep entity-lookup)
                (remove :common/disabled?)
                (cu/filter-by :entity-association/type :association.type/introducer-firm)
                (sort-by :common/created-on)
                (keep (fn [a]
                        (let [other-entity-id (associations/other-entity-id a (:db/id entity-data))
                              other-entity (entity-lookup other-entity-id)]
                          (when (= :entity.type/company (:entity/type other-entity))
                            other-entity))))
                (remove :common/disabled?)
                (first)
                :db/id)))
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/translations          {:en "Introducer firm"}}]]
    [[:oppo/calc-oldest-mandate-firm
      {:field/data-type             :data.type/ref-single
       :value-type                  :entity
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/link
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/entity-types          [:entity.type/company]
       :field/composite-attributes  [:common/related-entities
                                     :entity-association/source-entity
                                     :entity-association/target-entity]
       :calculation-triggers        (oldest-association-triggers :entity.type/company)
       :field/calculated-field
       (fn
         [{:keys [entity-data entity-lookup
                  reverse-lookup]}]
         (let [source-associations (reverse-lookup
                                    {:entity                 entity-data
                                     :attribute-entity-types [:entity.type/entity-association]
                                     :attribute              :entity-association/source-entity})
               target-associations (reverse-lookup
                                    {:entity                 entity-data
                                     :attribute-entity-types [:entity.type/entity-association]
                                     :attribute              :entity-association/target-entity})
               related-associations (concat source-associations
                                            target-associations)]
           (->> related-associations
                (keep entity-lookup)
                (remove :common/disabled?)
                (cu/filter-by :entity-association/type :association.type/mandate-firm)
                (sort-by :common/created-on)
                (keep (fn [a]
                        (let [other-entity-id (associations/other-entity-id a (:db/id entity-data))
                              other-entity (entity-lookup other-entity-id)]
                          (when (= :entity.type/company (:entity/type other-entity))
                            other-entity))))
                (remove :common/disabled?)
                (first)
                :db/id)))
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/translations          {:en "Mandate firm"}}]]
    [[:oppo/calc-oldest-deal-referrer
      {:field/data-type             :data.type/ref-single
       :value-type                  :entity
       :field/default-visibility    {:editor           :editor.type/none
                                     :grid             :editor.type/link
                                     :filter           :filter.type/select
                                     :field-visibility :not-in-use}
       :field/entity-types          [:entity.type/person]
       :field/composite-attributes  [:common/related-entities
                                     :entity-association/source-entity
                                     :entity-association/target-entity]
       :calculation-triggers        (oldest-association-triggers :entity.type/person)
       :field/calculated-field
       (fn
         [{:keys [entity-data entity-lookup
                  reverse-lookup]}]
         (let [source-associations (reverse-lookup
                                    {:entity                 entity-data
                                     :attribute-entity-types [:entity.type/entity-association]
                                     :attribute              :entity-association/source-entity})
               target-associations (reverse-lookup
                                    {:entity                 entity-data
                                     :attribute-entity-types [:entity.type/entity-association]
                                     :attribute              :entity-association/target-entity})
               related-associations (concat source-associations
                                            target-associations)]
           (->> related-associations
                (keep entity-lookup)
                (remove :common/disabled?)
                (cu/filter-by :entity-association/type :association.type/referrer-person)
                (sort-by :common/created-on)
                (keep (fn [a]
                        (let [other-entity-id (associations/other-entity-id a (:db/id entity-data))
                              other-entity (entity-lookup other-entity-id)]
                          (when (= :entity.type/person (:entity/type other-entity))
                            other-entity))))
                (remove :common/disabled?)
                (first)
                :db/id)))
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/system}
       :field/translations          {:en "Deal referrer"}}]]
    (su/option-field-multi :oppo/tag-liquidity-timing-estimate
                           "Liquidity timing estimate"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :oppo/tag-estimated-growth
                           "Estimated growth"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/option-field :oppo/deal-type
                     "Deal type"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :oppo/tag-brand
                           "Brand"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :oppo/tag-lead-market
                           "Lead market"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/currency-field :oppo/cur-y3-ins
                       "Y3 INS"
                       {:authorization            su/access-bsg-bsg
                        :default-field-visibility :not-in-use-blocked})
    (su/currency-field :oppo/cur-y3-ins-ra
                       "Y3 INS RA"
                       {:authorization            su/access-bsg-bsg
                        :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-request-received
                   "Request received"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-brief-completed
                   "Brief completed"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :oppo/tag-search-method
                           "Search method"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-search-completed
                   "Search completed"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-search-and-triage-completed
                   "Search & triage completed"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-outreach
                   "Outreach"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-due-diligence-begins
                   "Due diligence begins"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-due-diligence-ends
                   "Due diligence ends"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-go-no-go-decision
                   "Go/No Go decision"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-nbo-approval
                   "NBO approval"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-bo-approval
                   "BO approval"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/date-deal-finalization
                   "Deal finalization"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :oppo/tag-benefit-area
                           "Benefit area"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/calculated-date-difference :oppo/calc-time-to-complete-search
                                   "Time to complete search"
                                   :oppo/date-search-completed
                                   :oppo/date-brief-completed)

    (su/calculated-date-difference :oppo/calc-time-to-align-targets
                                   "Time to align target(s)"
                                   :oppo/date-search-and-triage-completed
                                   :oppo/date-search-completed)

    (su/calculated-date-difference :oppo/calc-time-to-start-DD
                                   "Time to start DD"
                                   :oppo/date-due-diligence-begins
                                   :oppo/date-search-and-triage-completed)

    (su/calculated-date-difference :oppo/calc-time-for-dd
                                   "Time for DD"
                                   :oppo/date-due-diligence-ends
                                   :oppo/date-due-diligence-begins)

    (su/calculated-date-difference :oppo/calc-time-to-go-no-go-decision
                                   "Time to Go/No Go Decision"
                                   :oppo/date-go-no-go-decision
                                   :oppo/date-due-diligence-ends)

    (su/calculated-date-difference :oppo/calc-time-to-NBO
                                   "Time to NBO"
                                   :oppo/date-nbo-approval
                                   :oppo/date-go-no-go-decision)

    (su/calculated-date-difference :oppo/calc-time-to-BO
                                   "Time to BO"
                                   :oppo/date-bo-approval
                                   :oppo/date-nbo-approval)

    (su/calculated-date-difference :oppo/calc-time-to-deal-finalization
                                   "Time to Deal Finalization"
                                   :oppo/date-deal-finalization
                                   :oppo/date-bo-approval)

    (su/option-field-multi :oppo/tag-quarterly-discussion-kpi
                           "Quarterly discussion KPI"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})

    (su/option-field-multi :oppo/partners
                           "Partners"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use})

    (su/date-field :oppo/date-signing-approval
                   "Signing approval date"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})

    (su/option-field :oppo/information-sharing
                     "Information sharing"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use-blocked})

    (su/option-field :oppo/public-funding
                     "Public funding"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})
    (su/single-ref-field :oppo/placement-holding
                         "Placement holding"
                         [:entity.type/holding]
                         {:authorization            su/access-bsg-bsg
                          :default-field-visibility :not-in-use})
    (su/single-ref-field :oppo/placement-company
                         "Placement company"
                         [:entity.type/company]
                         {:authorization            su/access-bsg-bsg
                          :default-field-visibility :not-in-use})
    (su/option-field :oppo/type
                     "Type"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})
    (su/currency-field :oppo/cur-revenues
                       "Revenues"
                       {:authorization            su/access-bsg-bsg
                        :default-field-visibility :not-in-use})

    (su/date-field :oppo/project-start-date
                   "Project Start Date"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use})
    (su/date-field :oppo/project-end-date
                   "Project End Date"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use})
    (su/integer-field :oppo/rank
                      "Rank"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use})

    (su/option-field :oppo/pmo-project-type
                     "PMO Project Type"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :oppo/project-description
                             "Project Description"
                             {:authorization            su/access-bsg-bsg
                              :default-field-visibility :not-in-use})
    (su/option-field-multi :oppo/internal-department
                           "Internal Department"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :oppo/cmt
                           "CMT"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :oppo/project-notes
                             "Project Notes"
                             {:authorization            su/access-bsg-bsg
                              :default-field-visibility :not-in-use})

    (su/integer-field :oppo/deal-number
                      "Deal Number"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use})
    (su/option-field :oppo/sector-select
                     "Sector"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})
    (su/text-multiline-field :oppo/sector-detail
                             "Sector Details"
                             {:authorization            su/access-bsg-bsg
                              :default-field-visibility :not-in-use})

    (su/integer-field :oppo/technology-readiness-level
                      "TRL (Technology Readiness Level)"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use})
    (su/integer-field :oppo/commercial-readiness-level
                      "CRL (Commercial Readiness Level)"
                      {:authorization            su/access-bsg-bsg
                       :default-field-visibility :not-in-use})
    (su/text-multiline-field :oppo/purpose-of-fundraising
                             "Purpose of Fundraising"
                             {:authorization            su/access-bsg-bsg
                              :default-field-visibility :not-in-use})

    (su/option-field :oppo/opt-use-of-proceeds
                     "Use of proceeds"
                     {:authorization            su/access-bsg-bsg
                      :default-field-visibility :not-in-use})
    (su/option-field :oppo/partner-decision
                     "Partner Decision"
                     {:default-field-visibility :not-in-use})

    (su/date-field :oppo/bid-deadline
                   "Bid deadline"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use})

    (su/option-field-multi :oppo/source-categories
                           "Source categories"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use})

    (su/multi-ref-field :oppo/risk-entities
                        "Risks"
                        [:entity.type/risk-holding]
                        {:authorization    su/access-user-user
                         :editor-type      :editor.type/none
                         :grid-type        :editor.type/none
                         :filter-type      :filter.type/boolean
                         :required-modules [:module/risk-module]})

    (su/option-field-multi :oppo/tag-sub-sector
                           "Sub sector"
                           {:authorization            su/access-bsg-bsg
                            :default-field-visibility :not-in-use-blocked})

    (su/single-ref-field :oppo/dd-budget-check
                         "DD budget check"
                         [:entity.type/pre-investment-check]
                         {:editor-type              :editor.type/none
                          :grid-type                :editor.type/none
                          :filter-type              :filter.type/none
                          :authorization            su/access-user-user
                          :default-field-visibility :not-in-use})

    (su/single-ref-field :oppo/fund-manager-check
                         "Fund manager check"
                         [:entity.type/pre-investment-check]
                         {:editor-type              :editor.type/none
                          :grid-type                :editor.type/none
                          :filter-type              :filter.type/none
                          :authorization            su/access-user-user
                          :default-field-visibility :not-in-use})

    (su/single-ref-field :oppo/legal-and-compliance-check
                         "Legal check"
                         [:entity.type/pre-investment-check]
                         {:editor-type              :editor.type/none
                          :grid-type                :editor.type/none
                          :filter-type              :filter.type/none
                          :authorization            su/access-user-user
                          :default-field-visibility :not-in-use})

    (su/single-ref-field :oppo/asset-kyc-check
                         "KYC check"
                         [:entity.type/pre-investment-check]
                         {:editor-type              :editor.type/none
                          :grid-type                :editor.type/none
                          :filter-type              :filter.type/none
                          :authorization            su/access-user-user
                          :default-field-visibility :not-in-use})

    (su/single-ref-field :oppo/esg-sfdr-check
                         "ESG check"
                         [:entity.type/pre-investment-check]
                         {:editor-type              :editor.type/none
                          :grid-type                :editor.type/none
                          :filter-type              :filter.type/none
                          :authorization            su/access-user-user
                          :default-field-visibility :not-in-use})

    (su/single-ref-field :oppo/risk-check
                         "Risk check"
                         [:entity.type/pre-investment-check]
                         {:editor-type              :editor.type/none
                          :grid-type                :editor.type/none
                          :filter-type              :filter.type/none
                          :authorization            su/access-user-user
                          :default-field-visibility :not-in-use})
    (su/date-field :oppo/expected-launch
                   "Expected launch"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    (su/date-field :oppo/expected-closing
                   "Expected closing"
                   {:authorization            su/access-bsg-bsg
                    :default-field-visibility :not-in-use-blocked})
    common-schema/common-folders-path
    common-schema/common-network-drives-field
    ;; leave this for easier merging
    )))
